/**
 * De staafgrafiek van het Gezondheid-scherm: gesprekken per kanaal,
 * per week, maand of jaar.
 *
 * Bewust met `div`-staven en geen SVG met vaste viewBox: een vaste
 * viewBox schaalt op 390 px naar onleesbaar klein, en een breedte die
 * niet meeschaalt geeft zijwaartse overloop. Deze staven zijn gewoon
 * flexkinderen, dus de grafiek past zich aan de kolom aan.
 *
 * De lopende periode staat gedempt en krijgt een sterretje met een
 * voetnoot: een halve week mag er nooit uitzien als een hele.
 */
import type {
  GezondheidKanaal,
  GezondheidStaaf,
} from "@/lib/werkplek-api";

interface Props {
  staven: GezondheidStaaf[];
  kanalen: GezondheidKanaal[];
  maximum: number;
  /** De normlijn: de mediaan van de afgeronde perioden (JP, punt 38). */
  norm?: number | null;
  normLabel?: string | null;
  redenLeeg: string | null;
}

/** De CSS-klasse van een kanaaldeel; de kleurrol komt van de backend. */
function deelKlasse(kanaal: GezondheidKanaal): string {
  return `wp-gz-deel-${kanaal.kleur === "blauw" ? "mail" : kanaal.kanaal}`;
}

/** Hoogste staaf, maar nooit nul: delen door nul geeft geen grafiek. */
function schaal(maximum: number): number {
  return maximum > 0 ? maximum : 1;
}

export function KanaalGrafiek({
  staven,
  kanalen,
  maximum,
  norm,
  normLabel,
  redenLeeg,
}: Props) {
  if (redenLeeg) {
    return (
      <p className="wp-gz-uitleg" role="status">
        {redenLeeg}
      </p>
    );
  }

  const noemer = schaal(maximum);
  const lopend = staven.find((staaf) => staaf.lopend);

  return (
    <div className="wp-gz-grafiek">
      <div
        className="wp-gz-staven"
        role="img"
        aria-label={`Gesprekken per kanaal, ${staven.length} perioden`}
      >
        {/* De normlijn ligt over de staven heen, op dezelfde schaal. */}
        {norm ? (
          <div
            className="wp-gz-norm"
            data-norm={norm}
            style={{ bottom: `${Math.min(100, (norm / noemer) * 100)}%` }}
          >
            <span>{normLabel ?? `norm ${norm}`}</span>
          </div>
        ) : null}
        {staven.map((staaf) => (
          <div
            key={staaf.sleutel}
            className="wp-gz-staaf"
            data-lopend={staaf.lopend ? "ja" : "nee"}
            title={`${staaf.label}${staaf.lopend ? " (tot nu toe)" : ""}: ${staaf.totaal}`}
          >
            {/* Het totaal boven de staaf (JP, punt 38 van 22-09): met
                alleen een hoogte moet je elk getal schatten. */}
            <span className="wp-gz-staaftotaal" data-totaal={staaf.sleutel}>
              {staaf.totaal || ""}
            </span>
            {/* Bovenaan het kleinste kanaal, zodat de mailbalk als
                grootste onderop staat en de staven vergelijkbaar zijn. */}
            {[...kanalen].reverse().map((kanaal) => {
              const aantal = staaf.per_kanaal[kanaal.kanaal] ?? 0;
              if (aantal === 0) return null;
              return (
                <i
                  key={kanaal.kanaal}
                  className={deelKlasse(kanaal)}
                  style={{ height: `${(aantal / noemer) * 100}%` }}
                />
              );
            })}
          </div>
        ))}
      </div>

      <div className="wp-gz-asrij" aria-hidden>
        {staven.map((staaf) => (
          <span key={staaf.sleutel}>
            {staaf.kort}
            {staaf.lopend ? "*" : ""}
          </span>
        ))}
      </div>

      <div className="wp-gz-legenda">
        {kanalen.map((kanaal) => (
          <span key={kanaal.kanaal}>
            <i className={deelKlasse(kanaal)} aria-hidden />
            {kanaal.label}
          </span>
        ))}
        {norm && normLabel ? (
          <span className="wp-gz-voetnoot" data-normlabel>
            Streeplijn: {normLabel}
          </span>
        ) : null}
        {lopend ? (
          <span className="wp-gz-voetnoot">
            * {lopend.label} tot nu toe
          </span>
        ) : null}
      </div>

      {/* Dezelfde cijfers als tekst, voor wie de grafiek niet ziet. Een
          tabel negeert de 1 px van sr-only (op iPhone 390 werd hij 386 px
          breed en schoof de pagina 26 px zijwaarts); de div knipt hem af
          met overflow hidden. */}
      <div className="sr-only" data-testid="kanaal-tabel-sr">
      <table>
        <caption>Gesprekken per periode en kanaal</caption>
        <thead>
          <tr>
            <th scope="col">Periode</th>
            {kanalen.map((kanaal) => (
              <th key={kanaal.kanaal} scope="col">
                {kanaal.label}
              </th>
            ))}
            <th scope="col">Totaal</th>
          </tr>
        </thead>
        <tbody>
          {staven.map((staaf) => (
            <tr key={staaf.sleutel}>
              <th scope="row">
                {staaf.label}
                {staaf.lopend ? " (tot nu toe)" : ""}
              </th>
              {kanalen.map((kanaal) => (
                <td key={kanaal.kanaal}>
                  {staaf.per_kanaal[kanaal.kanaal] ?? 0}
                </td>
              ))}
              <td>{staaf.totaal}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
